function parseMermaidFlowchart(code) {
    const labels = new Map();
    const order = [];
    const edges = [];
    const addNode = (id, label) => {
        if (!labels.has(id))
            order.push(id);
        labels.set(id, label || labels.get(id) || id);
    };
    for (const rawLine of code.split("\n")) {
        const line = rawLine.split("%%", 1)[0].trim().replace(/;$/, "");
        if (!line || /^(flowchart|graph)\s+/i.test(line) || /^subgraph\b|^end$/i.test(line))
            continue;
        for (const statement of line.split(";").map((item) => item.trim()).filter(Boolean)) {
            const parts = statement.split(/\s*(?:--(?:[^>-]*?)-->|-->|==>|-\.->|---)\s*/).filter(Boolean);
            if (parts.length < 2) {
                const parsed = parseMermaidNode(parts[0] ?? statement);
                if (parsed)
                    addNode(parsed.id, parsed.label);
                continue;
            }
            const parsedParts = parts.map((part) => parseMermaidNode(part.replace(/^\|[^|]*\|\s*/, "")));
            parsedParts.forEach((node) => {
                if (node)
                    addNode(node.id, node.label);
            });
            for (let index = 0; index < parsedParts.length - 1; index += 1) {
                const source = parsedParts[index];
                const target = parsedParts[index + 1];
                if (source && target)
                    edges.push({ source: source.id, target: target.id });
            }
        }
    }
    const layers = layoutMermaidNodes(order, edges);
    const nodes = order.map((id) => {
        const lines = wrapMermaidLabel(labels.get(id) ?? id);
        const width = Math.min(230, Math.max(118, Math.max(...lines.map((line) => line.length), 8) * 7.2 + 28));
        const height = Math.max(46, lines.length * 17 + 20);
        const position = layers.get(id) ?? { depth: 0, row: 0, rowCount: 1 };
        return {
            id,
            label: labels.get(id) ?? id,
            x: 110 + position.depth * 225,
            y: 80 + (position.row - (position.rowCount - 1) / 2) * 92,
            width,
            height,
            lines
        };
    });
    return { nodes, edges };
}

function parseMermaidNode(value) {
    const cleaned = value.trim().replace(/^&\w+?\s*/, "");
    const match = cleaned.match(/^([A-Za-z0-9_][\w-]*)(?:\s*(?:\[([^\]]+)\]|\(([^)]+)\)|\{([^}]+)\}))?$/);
    if (!match)
        return null;
    const rawLabel = match[2] ?? match[3] ?? match[4] ?? match[1];
    return { id: match[1], label: cleanMermaidLabel(rawLabel) };
}

function cleanMermaidLabel(label) {
    return label
        .trim()
        .replace(/^["']|["']$/g, "")
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<[^>]+>/g, "")
        .replace(/&nbsp;/g, " ");
}

function wrapMermaidLabel(label) {
    const lines = [];
    for (const rawLine of label.split("\n")) {
        const words = rawLine.trim().split(/\s+/).filter(Boolean);
        let current = "";
        for (const word of words) {
            if (`${current} ${word}`.trim().length > 24 && current) {
                lines.push(current);
                current = word;
            }
            else {
                current = `${current} ${word}`.trim();
            }
        }
        if (current)
            lines.push(current);
    }
    return lines.length ? lines : [label];
}

function layoutMermaidNodes(nodes, edges) {
    const children = new Map();
    const indegree = new Map(nodes.map((node) => [node, 0]));
    edges.forEach((edge) => {
        children.set(edge.source, [...(children.get(edge.source) ?? []), edge.target]);
        indegree.set(edge.target, (indegree.get(edge.target) ?? 0) + 1);
    });
    const depth = new Map(nodes.map((node) => [node, 0]));
    const queue = nodes.filter((node) => (indegree.get(node) ?? 0) === 0);
    while (queue.length) {
        const node = queue.shift();
        for (const child of children.get(node) ?? []) {
            depth.set(child, Math.max(depth.get(child) ?? 0, (depth.get(node) ?? 0) + 1));
            indegree.set(child, (indegree.get(child) ?? 1) - 1);
            if ((indegree.get(child) ?? 0) === 0)
                queue.push(child);
        }
    }
    const byDepth = new Map();
    nodes.forEach((node) => {
        const layer = depth.get(node) ?? 0;
        byDepth.set(layer, [...(byDepth.get(layer) ?? []), node]);
    });
    const layout = new Map();
    byDepth.forEach((layerNodes, layer) => {
        layerNodes.forEach((node, row) => layout.set(node, { depth: layer, row, rowCount: layerNodes.length }));
    });
    return layout;
}

export { parseMermaidFlowchart, parseMermaidNode, cleanMermaidLabel, wrapMermaidLabel, layoutMermaidNodes };
